import { atom, atomFamily } from "recoil";
import { HeaderActionOptions } from "../models/common/data";
import { FlexFormStatus } from "../models/generated/graphql";

type ColumnsValuesKeys = "agreements.columns" | "simulations.columns";

type FilterPopOverKeys =
	| "forms.filterPopOver"
	| "records.filterPopOver"
	| "cases.filterPopOver"
	| "agreements.filterPopOver"
	| "publicAgreements.filterPopOver"
	| "documents.filterPopOver"
	| "refunds.filterPopOver"
	| "quests.filterPopOver"
	| "models.filterPopOver";
type tableHeaderActionKeys =
	| "forms.activeTableHeaderActions"
	| "records.activeTableHeaderActions"
	| "cases.activeTableHeaderActions"
	| "agreements.activeTableHeaderActions"
	| "publicAgreements.activeTableHeaderActions"
	| "refunds.activeTableHeaderActions"
	| "documents.activeTableHeaderActions"
	| "simulations.activeTableHeaderActions";
type RightDrawerKeys = "forms.rightDrawer" | "records.rightDrawer" | "cases.rightDrawer" | "agreements.rightDrawer";

export interface ActiveTab {
	key: "activeDataTab";
	default: number;
}

export interface TableHeaderActions {
	key: tableHeaderActionKeys;
	default: {
		column: string | undefined;
		action: HeaderActionOptions | undefined;
	};
}

interface IRightDrawerState {
	key: RightDrawerKeys;
	default: false | IDrawerData;
}

interface IFilterPopOver {
	key: FilterPopOverKeys;
	default: HTMLElement | null;
}

interface IColumnsPopOver {
	key: ColumnsValuesKeys;
	default: HTMLElement | null;
}

export type TableColumns<TColumn = string> = {
	title: string;
	field: TColumn;
	default: boolean;
};

export type SimulatorSettingsTab = {
	key: string;
	default: number;
};

export interface IDrawerData {
	id?: string;
	_id?: string;
	__typename: "Form" | string; // update with other types
	preparationId: string;
	status: FlexFormStatus;
}

export const matchAllSearchTerm = "*";

export const addSearchTermCondition = (filters: object, searchTerm: string, language?: string) => {
	const searchField = () => {
		if (!language) {
			return "formattedContent_CONTAINS";
		}

		return language.startsWith("en") ? "viewFormattedContentEn_CONTAINS" : "viewFormattedContentDe_CONTAINS";
	};

	return searchTerm && searchTerm !== matchAllSearchTerm ? { ...filters, [searchField()]: searchTerm.toLowerCase() } : filters;
};

const initialActiveDataTabState: ActiveTab = {
	key: "activeDataTab",
	default: 0,
};

const initialFormsTableHeaderActionsState: TableHeaderActions = {
	key: "forms.activeTableHeaderActions",
	default: {
		column: undefined,
		action: undefined,
	},
};

export const getFieldNameByColumnName = (columnName: string) => columnName?.split(".")[0] || "";

export const getSortOrderByHeaderAction = (option: HeaderActionOptions) => {
	if (!option) {
		return undefined;
	}

	switch (option) {
		case "sortAsc":
			return "ASC";

		case "sortDesc":
			return "DESC";

		default:
			throw Error(`Is not a sorting action: ${option}`);
	}
};

const initialRecordsTableHeaderActionsState: TableHeaderActions = {
	key: "records.activeTableHeaderActions",
	default: {
		column: undefined,
		action: undefined,
	},
};

const initialCasesTableHeaderActionsState: TableHeaderActions = {
	key: "cases.activeTableHeaderActions",
	default: {
		column: undefined,
		action: undefined,
	},
};

const initialAgreementsTableHeaderActionsState: TableHeaderActions = {
	key: "agreements.activeTableHeaderActions",
	default: {
		column: undefined,
		action: undefined,
	},
};

const initialPublicAgreementsTableHeaderActionsState: TableHeaderActions = {
	key: "publicAgreements.activeTableHeaderActions",
	default: {
		column: undefined,
		action: undefined,
	},
};

const initialRefundsTableHeaderActionsState: TableHeaderActions = {
	key: "refunds.activeTableHeaderActions",
	default: {
		column: undefined,
		action: undefined,
	},
};

const initialDocumentsTableHeaderActionsState: TableHeaderActions = {
	key: "documents.activeTableHeaderActions",
	default: {
		column: undefined,
		action: undefined,
	},
};

const initialSimulationsTableHeaderActionsState: TableHeaderActions = {
	key: "simulations.activeTableHeaderActions",
	default: {
		column: undefined,
		action: undefined,
	},
};

const initialActiveSimulationSettingsTab: SimulatorSettingsTab = {
	key: "activeSettingsTab",
	default: 0,
};

const initialFormsIFilterPopOverState: IFilterPopOver = {
	key: "forms.filterPopOver",
	default: null,
};

const initialAgreementsColumnsPopOverState: IColumnsPopOver = {
	key: "agreements.columns",
	default: null,
};

const initialSimulationsColumnsPopOverState: IColumnsPopOver = {
	key: "simulations.columns",
	default: null,
};

const initialRecordsIFilterPopOverState: IFilterPopOver = {
	key: "records.filterPopOver",
	default: null,
};

const initialQuestsIFilterPopOverState: IFilterPopOver = {
	key: "quests.filterPopOver",
	default: null,
};

const initialCasesIFilterPopOverState: IFilterPopOver = {
	key: "cases.filterPopOver",
	default: null,
};

const initialAgreementsIFilterPopOverState: IFilterPopOver = {
	key: "agreements.filterPopOver",
	default: null,
};

const initialPublicAgreementsIFilterPopOverState: IFilterPopOver = {
	key: "publicAgreements.filterPopOver",
	default: null,
};

const initialModelsIFilterPopOverState: IFilterPopOver = {
	key: "models.filterPopOver",
	default: null,
};

const initialDocumentsIFilterPopOverState: IFilterPopOver = {
	key: "documents.filterPopOver",
	default: null,
};

const initialRefundsIFilterPopOverState: IFilterPopOver = {
	key: "refunds.filterPopOver",
	default: null,
};

const initialFormsRightDrawerState: IRightDrawerState = {
	key: "forms.rightDrawer",
	default: false,
};

const initialRecordsRightDrawerState: IRightDrawerState = {
	key: "records.rightDrawer",
	default: false,
};

const initialCasesRightDrawerState: IRightDrawerState = {
	key: "cases.rightDrawer",
	default: false,
};

const initialAgreementsRightDrawerState: IRightDrawerState = {
	key: "agreements.rightDrawer",
	default: false,
};

export enum FilterOperators {
	Equal = "_EQUAL",
	StartsWith = "STARTS_WITH",
	EndsWith = "ENDS_WITH",
	GreaterOrEqual = "GTE",
	LessOrEqual = "LTE",
	In = "IN",
	NotIn = "NOT_IN",
	Regexp = "CONTAINS",
	Is = "IS",
	IsNot = "IS_NOT",
}

export const stringOperatorOptions = [FilterOperators.Regexp, FilterOperators.StartsWith, FilterOperators.EndsWith] as const;
export const numOperatorOptions = [FilterOperators.GreaterOrEqual, FilterOperators.LessOrEqual];
export const enumOperatorOptions = [FilterOperators.NotIn, FilterOperators.In];
export const dateOperatorOptions = [FilterOperators.In];
export const operatorOptions = {
	stringOperatorOptions: stringOperatorOptions,
	numOperatorOptions: numOperatorOptions,
	enumOperatorOptions: enumOperatorOptions,
	dateOperatorOptions: dateOperatorOptions,
};
export const activeDataTabState = atom(initialActiveDataTabState);
export const formsTableHeaderActionsState = atom(initialFormsTableHeaderActionsState);
export const recordsTableHeaderActionsState = atom(initialRecordsTableHeaderActionsState);
export const casesTableHeaderActionsState = atom(initialCasesTableHeaderActionsState);
export const agreementsTableHeaderActionsState = atom(initialAgreementsTableHeaderActionsState);
export const publicAgreementsTableHeaderActionsState = atom(initialPublicAgreementsTableHeaderActionsState);
export const refundsTableHeaderActionsState = atom(initialRefundsTableHeaderActionsState);
export const documentsTableHeaderActionsState = atom(initialDocumentsTableHeaderActionsState);
export const questsIFilterPopOverState = atom(initialQuestsIFilterPopOverState);
export const formsIFilterPopOverState = atom(initialFormsIFilterPopOverState);
export const recordsIFilterPopOverState = atom(initialRecordsIFilterPopOverState);
export const casesIFilterPopOverState = atom(initialCasesIFilterPopOverState);
export const agreementsColumnsPopOverState = atom(initialAgreementsColumnsPopOverState);
export const simulationsColumnsPopOverState = atom(initialSimulationsColumnsPopOverState);
export const formsRightDrawerState = atom(initialFormsRightDrawerState);
export const recordsRightDrawerState = atom(initialRecordsRightDrawerState);
export const casesRightDrawerState = atom(initialCasesRightDrawerState);
export const agreementsRightDrawerState = atom(initialAgreementsRightDrawerState);
export const agreementsIFilterPopOverState = atom(initialAgreementsIFilterPopOverState);
export const publicAgreementsIFilterPopOverState = atom(initialPublicAgreementsIFilterPopOverState);
export const modelsIFilterPopOverState = atom(initialModelsIFilterPopOverState);
export const documentsIFilterPopOverState = atom(initialDocumentsIFilterPopOverState);
export const refundsIFilterPopOverState = atom(initialRefundsIFilterPopOverState);
export const simulationsTableHeaderActionsState = atom(initialSimulationsTableHeaderActionsState);
export const simulationSettingsTabState = atom(initialActiveSimulationSettingsTab);

export const filterStateFamily = atomFamily({
	key: "FilterState",
	default: {} as any,
});
